<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import { useFlowSteps } from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import { useMppPolicyLabel } from '@Use/useMppPolicyLabel';

// Import stores
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';

// Import API
import { getInsuranceOptionsApi } from '@Api/requestFlow.api';
import type { MppPolicy } from '@Types/MppPolicy';

// Import tracking
import { trackTransferAmountSelected } from '@Analytics/request.mixpanel';

// Import used components
import { FlowLayout } from '@Shared';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const { advanceFlow, goBackInFlow } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { formatPolicyLabel } = useMppPolicyLabel();
const { taskNavTitle } = useTaskNavTitle();

// Page state
const isNavigating = ref(false);
const isLoading = ref(false);
const errorMessage = ref<string | null>(null);
const insuranceOptions = ref<MppPolicy[]>([]);
const selectedPolicy = ref<MppPolicy | null>(null);

// Map policies to dropdown-friendly objects
const policyOptions = computed(() =>
    insuranceOptions.value.map((policy) => ({
        id: policy.mppPolicyId,
        label: formatPolicyLabel(policy),
        policy,
    })),
);

// The selected dropdown item (bound to app-dropdown via id-field/display-field)
const selectedOption = computed({
    get() {
        if (!selectedPolicy.value) return null;
        return (
            policyOptions.value.find(
                (o) => o.id === selectedPolicy.value!.mppPolicyId,
            ) ?? null
        );
    },
    set(option: { id: string; label: string; policy: MppPolicy } | null) {
        selectedPolicy.value = option?.policy ?? null;
    },
});

// Actions
const continueFlow = async () => {
    if (isNavigating.value || !selectedPolicy.value) return;

    isNavigating.value = true;
    errorMessage.value = null;

    try {
        requestSessionStore.selectedMppPolicy = selectedPolicy.value;

        trackTransferAmountSelected(formatPolicyLabel(selectedPolicy.value));
        advanceFlow();
    } catch {
        errorMessage.value = 'Something went wrong. Please try again.';
        isNavigating.value = false;
    }
};

// Lifecycle
onMounted(async () => {
    isLoading.value = true;

    try {
        insuranceOptions.value = await getInsuranceOptionsApi();
    } catch {
        errorMessage.value =
            'Failed to load transfer amount options. Please refresh and try again.';
    } finally {
        isLoading.value = false;
    }

    // Restore previous selection if the user reloaded
    if (requestSessionStore.selectedMppPolicy) {
        const restored = insuranceOptions.value.find(
            (p) =>
                p.mppPolicyId ===
                requestSessionStore.selectedMppPolicy!.mppPolicyId,
        );
        if (restored) {
            selectedPolicy.value = restored;
        }
    }
});
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <app-form-content-layout>
            <h1 bottomOffset="1rem">
                Estimate your transfer amount
            </h1>

            <p class="mpp-transfer-amount__description">
                This will calculate a quote for the optional <strong>Money Protection Plan</strong>,
                the highest level of protection available. Pricing starts at $20
                for protection of up to $50,000.
            </p>

            <app-dropdown
                v-model="selectedOption"
                empty-text="Select"
                id-field="id"
                display-field="label"
                :options="policyOptions"
                :is-disabled="isLoading"
                data-id="mpp-transfer-amount-select"
            />

            <p
                v-if="errorMessage"
                class="mpp-transfer-amount__error"
                role="alert"
            >
                {{ errorMessage }}
            </p>

            <app-button
                expanded
                size="large"
                :is-loading="isNavigating"
                :is-disabled="!selectedPolicy || isLoading"
                data-id="mpp-transfer-amount-continue"
                @on-click="continueFlow"
            >
                Continue
            </app-button>
        </app-form-content-layout>
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.mpp-transfer-amount {
    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
    }
}
</style>
